'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import {
  GroupHeaderCell,
  getEndpointApiTypeLabel,
} from '@terra/ui-design-system';
import { Globe02Icon } from '@terra/ui-icons';
import type {
  AssetPreviewDto,
  AssetsSearchGroupBy,
  AssetsSearchOrderByField,
} from '@terra/shared-contracts-assets';
import {
  ASSETS_TABLE_KIND,
  getAssetDomain,
  getUnverifiedApiTypeLabel,
  isAssetsTableFieldSortable,
  isAssetsTableGroupHeaderRow,
  type AssetsTableKind,
  type AssetsTableRow,
} from './assets-table.model';
import { useAssetsTableContext } from './AssetsTableContext';
import {
  AssetsTableSelectAllHeader,
  AssetsTableSelectionCell,
} from './AssetsTableSelectionCells';
import {
  EndpointGraphCell,
  EndpointNameCell,
  UnverifiedEndpointNameCell,
  WebpageNameCell,
} from './AssetsTableNameCells';
import {
  AssetApiTypeCell,
  AssetCreatedAtCell,
  AssetMethodCell,
  AssetRolesCell,
  AssetScreenshotCell,
  AssetSourcesCell,
  AssetTextCell,
} from './AssetsTableValueCells';
import { AssetDetectionsCell } from './AssetDetectionsCell';
import { WebpageRelatedEndpointsCell } from './WebpageRelatedEndpointsCell';
import { rowClickBlockerProps } from '../rowClickBlocker';
import type { AssetsDetectionView } from '../../../store/tab/assetsTabScopedFilterStore';

const DISPLAY_ONLY_FIELDS = ['screenshot', 'codeGraph'] as const;

type DisplayOnlyField = (typeof DISPLAY_ONLY_FIELDS)[number];

type AssetCellContext = {
  detectionView: AssetsDetectionView;
};

type AssetCellRender = (
  asset: AssetPreviewDto,
  context: AssetCellContext,
) => ReactNode;

type AssetColumn = {
  field: AssetsSearchOrderByField | DisplayOnlyField;
  headerKey: string;
  width?: number;
  flex?: number;
  minWidth: number;
  render: AssetCellRender;
};

const SELECT_COLUMN_WIDTH = 40;

const renderAssetCell =
  ({
    render,
    context,
  }: {
    render: AssetCellRender;
    context: AssetCellContext;
  }) =>
  (params: ICellRendererParams<AssetsTableRow>) => {
    if (!params.data || isAssetsTableGroupHeaderRow(params.data)) {
      return null;
    }

    return (
      <Box h="full" display="flex" alignItems="center" minW={0}>
        {render(params.data, context)}
      </Box>
    );
  };

const resolveDetectionCount = ({
  active,
  inactive,
  detectionView,
}: {
  active: number | undefined;
  inactive: number | undefined;
  detectionView: AssetsDetectionView;
}) =>
  detectionView === 'active' ? (active ?? 0) : (active ?? 0) + (inactive ?? 0);

const renderNameCell = (asset: AssetPreviewDto) => {
  switch (asset.assetType) {
    case 'endpoint':
      return <EndpointNameCell asset={asset} />;
    case 'webpage':
      return <WebpageNameCell asset={asset} />;
    case 'unverifiedEndpoint':
      return <UnverifiedEndpointNameCell asset={asset} />;
  }
};

const renderMethodCell = (asset: AssetPreviewDto) =>
  asset.assetType === 'webpage' ? null : (
    <AssetMethodCell method={asset.request.method} />
  );

const renderRolesCell = (asset: AssetPreviewDto) => {
  if (asset.assetType === 'endpoint') {
    return <AssetRolesCell roles={asset.credentialRoles ?? []} />;
  }

  return (
    <AssetRolesCell
      roles={asset.assetType === 'webpage' && asset.role ? [asset.role] : []}
    />
  );
};

const renderApiTypeCell = (asset: AssetPreviewDto) => {
  if (asset.assetType === 'webpage') {
    return null;
  }

  const label =
    asset.assetType === 'unverifiedEndpoint'
      ? getUnverifiedApiTypeLabel(asset.apiType)
      : getEndpointApiTypeLabel({
          ...asset,
          assetTags: asset.endpointAssetTags ?? [],
        });

  return <AssetApiTypeCell label={label} />;
};

const renderDetectionsCell: AssetCellRender = (asset, { detectionView }) =>
  asset.assetType === 'endpoint' ? (
    <AssetDetectionsCell
      endpointId={asset.id}
      detectionView={detectionView}
      findingsCount={resolveDetectionCount({
        active: asset.metrics?.activeFindingsCount,
        inactive: asset.metrics?.inactiveFindingsCount,
        detectionView,
      })}
      signalsCount={resolveDetectionCount({
        active: asset.metrics?.activeSignalsCount,
        inactive: asset.metrics?.inactiveSignalsCount,
        detectionView,
      })}
    />
  ) : null;

const renderRelatedWebpagesCell = (asset: AssetPreviewDto) => {
  if (asset.assetType !== 'endpoint') {
    return null;
  }

  const preview = asset.relatedWebpagePreview ?? null;

  return (
    <AssetScreenshotCell
      request={
        preview
          ? { source: 'endpoint', endpointId: asset.id, page: preview }
          : null
      }
      additionalCount={
        preview
          ? (asset.relatedWebpagesCount ?? asset.webpageIds.length) - 1
          : 0
      }
    />
  );
};

const renderWebpageScreenshotCell = (asset: AssetPreviewDto) =>
  asset.assetType === 'webpage' ? (
    <AssetScreenshotCell
      request={
        asset.signedUrl
          ? {
              source: 'webpage',
              page: {
                id: asset.id,
                url: asset.url,
                signedUrl: asset.signedUrl,
              },
            }
          : null
      }
    />
  ) : null;

const renderRelatedEndpointsCell = (asset: AssetPreviewDto) =>
  asset.assetType === 'webpage' ? (
    <WebpageRelatedEndpointsCell
      webpageId={asset.id}
      endpointsCount={asset.endpointsCount}
    />
  ) : null;

const renderSourcesCell = (asset: AssetPreviewDto) =>
  asset.assetType === 'endpoint' ? (
    <AssetSourcesCell sources={asset.sources ?? []} />
  ) : null;

const renderCodeGraphCell = (asset: AssetPreviewDto) =>
  asset.assetType === 'endpoint' ? <EndpointGraphCell asset={asset} /> : null;

const isDisplayOnlyField = (field: string): field is DisplayOnlyField =>
  DISPLAY_ONLY_FIELDS.some((displayOnlyField) => displayOnlyField === field);

const NAME_COLUMN: AssetColumn = {
  field: 'name',
  headerKey: 'name',
  flex: 1,
  minWidth: 280,
  render: renderNameCell,
};
const DOMAIN_COLUMN: AssetColumn = {
  field: 'domain',
  headerKey: 'domain',
  width: 200,
  minWidth: 140,
  render: (asset) => <AssetTextCell value={getAssetDomain(asset)} />,
};
const METHOD_COLUMN: AssetColumn = {
  field: 'method',
  headerKey: 'method',
  width: 96,
  minWidth: 80,
  render: renderMethodCell,
};
const ROLES_COLUMN: AssetColumn = {
  field: 'roles',
  headerKey: 'roles',
  width: 120,
  minWidth: 100,
  render: renderRolesCell,
};
const CREATED_AT_COLUMN: AssetColumn = {
  field: 'createdAt',
  headerKey: 'createdAt',
  width: 160,
  minWidth: 130,
  render: (asset) => <AssetCreatedAtCell createdAt={asset.createdAt} />,
};
const API_TYPE_COLUMN: AssetColumn = {
  field: 'apiType',
  headerKey: 'apiType',
  width: 96,
  minWidth: 88,
  render: renderApiTypeCell,
};

const COLUMNS_BY_KIND: Record<AssetsTableKind, readonly AssetColumn[]> = {
  [ASSETS_TABLE_KIND.ENDPOINTS]: [
    NAME_COLUMN,
    METHOD_COLUMN,
    {
      field: 'detections',
      headerKey: 'detections',
      width: 152,
      minWidth: 120,
      render: renderDetectionsCell,
    },
    ROLES_COLUMN,
    {
      field: 'sources',
      headerKey: 'source',
      width: 96,
      minWidth: 80,
      render: renderSourcesCell,
    },
    {
      field: 'relatedWebpages',
      headerKey: 'relatedWebpages',
      width: 96,
      minWidth: 88,
      render: renderRelatedWebpagesCell,
    },
    API_TYPE_COLUMN,
    {
      field: 'codeGraph',
      headerKey: 'codeGraph',
      width: 110,
      minWidth: 100,
      render: renderCodeGraphCell,
    },
    DOMAIN_COLUMN,
    CREATED_AT_COLUMN,
  ],
  [ASSETS_TABLE_KIND.WEB_PAGES]: [
    { ...NAME_COLUMN, field: 'url', headerKey: 'url' },
    DOMAIN_COLUMN,
    {
      field: 'screenshot',
      headerKey: 'screenshot',
      width: 130,
      minWidth: 110,
      render: renderWebpageScreenshotCell,
    },
    ROLES_COLUMN,
    CREATED_AT_COLUMN,
    {
      field: 'relatedEndpoints',
      headerKey: 'relatedEndpoints',
      width: 170,
      minWidth: 140,
      render: renderRelatedEndpointsCell,
    },
  ],
  [ASSETS_TABLE_KIND.UNVERIFIED]: [
    { ...NAME_COLUMN, field: 'url' },
    DOMAIN_COLUMN,
    METHOD_COLUMN,
    CREATED_AT_COLUMN,
    API_TYPE_COLUMN,
  ],
};

const preserveServerSortOrder = () => 0;

export const isAssetsTableSortColumn = (
  colId: string | undefined,
): colId is AssetsSearchOrderByField =>
  colId !== undefined &&
  !isDisplayOnlyField(colId) &&
  Object.values(COLUMNS_BY_KIND)
    .flat()
    .some((column) => column.field === colId);

const isColumnHiddenByGrouping = ({
  column,
  groupBy,
}: {
  column: AssetColumn;
  groupBy: AssetsSearchGroupBy | undefined;
}): boolean => groupBy === 'domain' && column.field === 'domain';

export function useAssetsTableColumnDefs({
  kind,
  groupBy,
  detectionView,
}: {
  kind: AssetsTableKind;
  groupBy: AssetsSearchGroupBy | undefined;
  detectionView: AssetsDetectionView;
}): ColDef<AssetsTableRow>[] {
  const t = useTranslations('proxy.assets.table');
  const { collapsedGroupKeys, onToggleGroupCollapsed } =
    useAssetsTableContext();

  return useMemo(() => {
    const columns = COLUMNS_BY_KIND[kind];
    const cellContext: AssetCellContext = { detectionView };
    const dataColumns = columns.map<ColDef<AssetsTableRow>>(
      (column, index) => ({
        colId: column.field,
        headerName: t(column.headerKey),
        width: column.width,
        flex: column.flex,
        minWidth: column.minWidth,
        hide: isColumnHiddenByGrouping({ column, groupBy }),
        lockPosition: index === 0 ? 'left' : undefined,
        sortable:
          !isDisplayOnlyField(column.field) &&
          isAssetsTableFieldSortable({ kind, field: column.field }),
        comparator: preserveServerSortOrder,
        colSpan: (params) =>
          index === 0 && isAssetsTableGroupHeaderRow(params.data)
            ? columns.length
            : 1,
        cellRenderer:
          index === 0
            ? (params: ICellRendererParams<AssetsTableRow>) => {
                if (isAssetsTableGroupHeaderRow(params.data)) {
                  return (
                    <GroupHeaderCell
                      group={params.data}
                      isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
                      onToggle={onToggleGroupCollapsed}
                      containerProps={rowClickBlockerProps}
                      showToggle={false}
                      icon={
                        groupBy === 'domain' ? (
                          <Box p="0.5" display="inline-flex">
                            <Globe02Icon size={16} />
                          </Box>
                        ) : undefined
                      }
                    />
                  );
                }

                return renderAssetCell({
                  render: column.render,
                  context: cellContext,
                })(params);
              }
            : renderAssetCell({ render: column.render, context: cellContext }),
      }),
    );

    return [
      {
        colId: 'select',
        headerName: '',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponent: AssetsTableSelectAllHeader,
        cellRenderer: (params: ICellRendererParams<AssetsTableRow>) => (
          <AssetsTableSelectionCell row={params.data} />
        ),
      },
      ...dataColumns,
    ];
  }, [
    collapsedGroupKeys,
    detectionView,
    groupBy,
    kind,
    onToggleGroupCollapsed,
    t,
  ]);
}
